import { type Blob, type Finding } from '@consigli/types';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';

import { attachedContextKind, findingContextKind } from './add-context/finding-context-kind';
import { type AddContextMenuKind, type AddContextSelection } from './types';

/**
 * The most one question can carry, matching the cap the add-context modal
 * enforces on a single selection. Applied here as well because the finding
 * cards attach one at a time, which the modal never sees.
 */
export const MAX_ATTACHMENTS = 100;

interface AgentChatAttachmentsContextValue {
  /** What is attached to the next question. */
  attachments: AddContextSelection;
  /**
   * The kind already attached, if any. A question carries one kind, so this is
   * what the add-context menu greys the other kinds out against. Findings,
   * milestones and response requirements count as three kinds.
   */
  attachedKind: AddContextMenuKind | null;
  /**
   * Attaches what a caller picked - the modal's selection, or a single finding
   * from its card on the Findings page. Ids already attached are ignored, so
   * attaching the same finding twice is a no-op rather than a duplicate chip.
   */
  addContext: (selection: AddContextSelection) => void;
  /**
   * Replaces what is attached with `selection` outright, rather than adding to
   * it. What the add-context modal submits: it opens with the attached rows
   * already ticked, so its selection is the whole answer - a row the user
   * unticked there has to come off the question, which adding could not do.
   */
  setContext: (selection: AddContextSelection) => void;
  /** Detaches everything, what the composer's "Clear all" does. */
  clearContext: () => void;
  removeDocument: (document: Blob) => void;
  removeFinding: (finding: Finding) => void;
}

const EMPTY: AddContextSelection = { documents: [], findings: [] };

/** The findings of the first one's kind, so a selection carries one kind even when handed a mix. */
const findingsOfOneKind = (findings: Finding[]): Finding[] => {
  if (findings.length === 0) return findings;
  const kind = findingContextKind(findings[0]);
  return findings.filter((entry) => findingContextKind(entry) === kind);
};

const AgentChatAttachmentsContext = createContext<AgentChatAttachmentsContextValue | null>(null);

/**
 * Holds what is attached to the next question, for everyone who can attach to
 * it or read it.
 *
 * Above the router's Outlet rather than beside it, which is where
 * `AgentChatSessionProvider` sits: the chat drawer unmounts whenever it is
 * closed, so state kept inside it cannot survive a finding being attached from
 * a page behind it - nor the drawer simply being closed and reopened.
 *
 * Kept apart from the session provider rather than folded into it because that
 * one re-renders on every streamed character, and is deliberately mounted low
 * so the workspace page behind it does not. Attaching is rare; streaming is
 * not.
 */
export const AgentChatAttachmentsProvider: FC<{ children: ReactNode; packageId: string }> = ({
  children,
  packageId,
}) => {
  const [attachments, setAttachments] = useState<AddContextSelection>(EMPTY);
  const [seededPackageId, setSeededPackageId] = useState(packageId);

  /*
   * Attachments belong to the package they were picked from. The router reuses
   * this element across a `:packageId` change rather than remounting it - the
   * session provider swaps its stored history for the same reason - so without
   * this, a finding attached in one package would be sent as the context for a
   * question asked in the next one.
   *
   * Adjusting state during render is the supported way to do that, so the reset
   * lands in the same paint as the new package.
   */
  if (seededPackageId !== packageId) {
    setSeededPackageId(packageId);
    setAttachments(EMPTY);
  }

  const addContext = useCallback((selection: AddContextSelection) => {
    setAttachments((previous) => {
      /*
       * A question carries one kind, so whichever kind is being attached now
       * replaces the other rather than sitting alongside it. The add-context
       * menu already stops this being reachable from the composer - it greys
       * out the kind that is not attached - but a finding card sits on a page
       * that cannot see the menu, so the rule is enforced where the state is.
       */
      if (selection.findings.length > 0) {
        const incoming = findingsOfOneKind(selection.findings);
        const kind = findingContextKind(incoming[0]);
        // Milestones, response requirements and findings replace one another too.
        const kept = previous.findings.filter((entry) => findingContextKind(entry) === kind);
        const seen = new Set(kept.map((entry) => entry.id));

        return {
          documents: [],
          findings: [...kept, ...incoming.filter((entry) => !seen.has(entry.id))].slice(
            0,
            MAX_ATTACHMENTS,
          ),
        };
      }

      if (selection.documents.length > 0) {
        const seen = new Set(previous.documents.map((entry) => entry.id));

        return {
          documents: [
            ...previous.documents,
            ...selection.documents.filter((entry) => !seen.has(entry.id)),
          ].slice(0, MAX_ATTACHMENTS),
          findings: [],
        };
      }

      return previous;
    });
  }, []);

  const setContext = useCallback((selection: AddContextSelection) => {
    /*
     * One kind at a time here too, and findings win a selection carrying both,
     * matching addContext. An empty selection clears the question, which is
     * what unticking every row in the modal and submitting means.
     */
    setAttachments(
      selection.findings.length > 0
        ? {
            documents: [],
            findings: findingsOfOneKind(selection.findings).slice(0, MAX_ATTACHMENTS),
          }
        : { documents: selection.documents.slice(0, MAX_ATTACHMENTS), findings: [] },
    );
  }, []);

  const clearContext = useCallback(() => setAttachments(EMPTY), []);

  const removeDocument = useCallback((document: Blob) => {
    setAttachments((previous) => ({
      ...previous,
      documents: previous.documents.filter((entry) => entry.id !== document.id),
    }));
  }, []);

  const removeFinding = useCallback((finding: Finding) => {
    setAttachments((previous) => ({
      ...previous,
      findings: previous.findings.filter((entry) => entry.id !== finding.id),
    }));
  }, []);

  const value = useMemo<AgentChatAttachmentsContextValue>(
    () => ({
      attachments,
      attachedKind: attachedContextKind(attachments),
      addContext,
      setContext,
      clearContext,
      removeDocument,
      removeFinding,
    }),
    [addContext, attachments, clearContext, removeDocument, removeFinding, setContext],
  );

  return (
    <AgentChatAttachmentsContext.Provider value={value}>
      {children}
    </AgentChatAttachmentsContext.Provider>
  );
};

export const useAgentChatAttachments = (): AgentChatAttachmentsContextValue => {
  const value = useContext(AgentChatAttachmentsContext);

  if (!value) throw new Error('Missing AgentChatAttachmentsProvider in tree');

  return value;
};
